import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, BookOpen, Building2, DollarSign, Zap, AlertOctagon, LifeBuoy,
  ShieldCheck, Activity, Search, KeyRound, HeartPulse,
} from 'lucide-react';

const Card = ({ children, testid }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-3" data-testid={testid}>{children}</div>
);
const Title = ({ icon: Icon, color, children }) => (
  <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
    <Icon className="w-5 h-5" style={{ color }} />{children}
  </h2>
);
const P = ({ children }) => <p className="text-sm text-slate-300 leading-relaxed">{children}</p>;
const Ul = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-300 leading-relaxed">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);
const B = ({ children }) => <strong className="text-slate-100">{children}</strong>;
const Warn = ({ children }) => (
  <div className="rounded-lg border border-amber-700/60 bg-amber-950/40 p-3 text-sm text-amber-200 leading-relaxed">{children}</div>
);

export default function PlatformManual() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pb-20" data-testid="platform-manual-page">
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button onClick={() => navigate('/platform-admin/dashboard')} variant="ghost" size="sm" className="text-slate-300 hover:text-white" data-testid="platform-manual-back-btn">
            <ArrowLeft className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Dashboard</span>
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <BookOpen className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-lg font-bold truncate">Platform HQ Manual</h1>
              <p className="text-xs text-slate-400 truncate">Scalevisory operators only · v1.0 · Aug 2026</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <Card testid="pm-overview">
          <Title icon={Building2} color="#1E88E5">1. Dashboard overview</Title>
          <P>The main dashboard is the health monitor for every customer company. Top tiles: <B>Companies</B> (+ signups this week), <B>Total Users</B>, <B>On Trial</B>, <B>Trial Pipeline</B> (potential MRR from expired trials), <B>Paying</B> (with live MRR estimate), <B>Suspended</B>, and <B>Jobs · 7d</B> (platform-wide activity).</P>
          <Ul items={[
            <>Charts row: <B>MRR · last 12 months</B>, <B>Signups · 30 days</B>, and <B>Conversions · last 8 weeks</B> (new paying subs + MRR added, "This week" chip).</>,
            <>The <B>Conversion Funnel</B> card tracks grace started → checkout created → paid over the last 30 days, with step-to-step rates.</>,
            <>The <B>Recovery report</B> panel (inside the enforcement card) shows grace starts, reminders, win-backs, lockouts, and how many companies recovered before/after lockout.</>,
          ]} />
        </Card>

        <Card testid="pm-companies">
          <Title icon={Search} color="#8B5CF6">2. Companies table</Title>
          <Ul items={[
            <>Filter by status (trial / active / grace / past due / suspended) or free-text search by name/email.</>,
            <>Each row shows <B>trial countdown</B> ("Nd left" / "expired Nd ago"), grace end date, seat count, and a <B>billing exempt</B> tag when applicable.</>,
            <>Row actions: <B>History</B> opens the billing timeline (every grace, reminder, charge, lockout, win-back event, newest first). <B>ShieldOff</B> toggles billing exemption — exempt companies are never charged, reminded, or locked out.</>,
            <>Support tools (⋯ menu): <B>Edit Company</B> (name, email, status), <B>Edit Super User</B> (name, email, password reset), <B>Delete</B> (soft delete, restorable via <B>Restore</B>).</>,
          ]} />
        </Card>

        <Card testid="pm-billing">
          <Title icon={DollarSign} color="#2CA01C">3. Billing enforcement &amp; lifecycle</Title>
          <P>The <B>enforcement card</B> controls automatic billing. The lifecycle when ON: trial expires → card on file is charged (seats × $5.99/mo, annual $59.90/seat available) → no card / declined → email + <B>grace period</B> (7 days; 14 for legacy) → reminder at 3 days left → <B>locked out</B> if unpaid → one-time <B>win-back email</B> with a one-click restore link after 7 days locked. A <B>checkout nudge</B> email goes out if a company started checkout but didn't finish within 20h.</P>
          <Ul items={[
            <><B>Turn on / off</B> — owner only. Turning it ON in production starts the hourly sweep immediately.</>,
            <><B>Preview sweep</B> — a dry run that shows exactly which companies would be charged, sent to grace, reminded, or locked out. Always preview before flipping enforcement on.</>,
            <>A <B>dunning digest</B> email summarising billing events is sent to owners at most once per 24h after sweeps.</>,
            <>Seat counts <B>auto-sync</B> to Stripe when customers add/archive staff — a seat-change receipt is emailed to the customer.</>,
          ]} />
          <Warn>Never toggle enforcement in Preview — the sweep would email real customers. Enforcement actions belong in production Platform HQ only.</Warn>
        </Card>

        <Card testid="pm-stripe">
          <Title icon={Zap} color="#F59E0B">4. Stripe health &amp; webhooks</Title>
          <Ul items={[
            <>The <B>Stripe connected / DOWN</B> light next to the enforcement badge shows live API reachability (hover for the error when red).</>,
            <>A red <B>Webhook stale</B> banner appears when no Stripe event has been received for 24+ hours — payments may be succeeding without activating accounts.</>,
            <><B>Repair webhook</B> re-creates the Stripe webhook endpoint and stores a fresh signing secret. <B>Production only</B> — the button is blocked in Preview by design.</>,
            <><B>Webhook log</B> opens the event history: every received Stripe event with type, processing status, and live/test mode. Use it to confirm a customer's payment event actually arrived.</>,
            <>If a customer paid but their banner stays, have them open their Billing page — it self-heals by syncing the subscription from Stripe on load.</>,
          ]} />
        </Card>

        <Card testid="pm-errors">
          <Title icon={AlertOctagon} color="#EF4444">5. Error Watch (Errors page)</Title>
          <Ul items={[
            <>Header → <B>Errors</B>. Live feed of client-side errors from every customer browser, grouped with filters by company, route, and date.</>,
            <>The <B>Top pages by error volume (7d)</B> chips show where users are hurting most — click a chip to filter to that route.</>,
            <>Mark rows <B>resolved</B> once fixed. Errors auto-expire after 90 days.</>,
            <>An <B>error email digest</B> (totals, top pages, top tenants, samples) is emailed to owners at most once per 24h when new errors exist. Manual trigger: the send-digest action on the Errors page.</>,
          ]} />
        </Card>

        <Card testid="pm-support">
          <Title icon={LifeBuoy} color="#0EA5E9">6. Support page</Title>
          <Ul items={[
            <>Header → <B>Support</B>. Look up any user by email/name across all companies — see their company, role, status, and 2FA state.</>,
            <>The <B>Reset Health</B> panel shows password-reset activity per email: requests, completions, failure reasons (expired token, weak password, rate-limited…), and a live <B>sign-in locked</B> flag (5 wrong passwords → 15-min lock, clears on successful reset). <B>Find</B> jumps to that user's lookup.</>,
            <>Use this first when a customer says "I can't log in / my reset link doesn't work" — the failure reason is usually right there.</>,
          ]} />
        </Card>

        <Card testid="pm-security">
          <Title icon={KeyRound} color="#EC4899">7. Access &amp; security</Title>
          <Ul items={[
            <>Platform HQ is restricted to <B>@scalevisory.com</B> accounts. Roles: <B>owner</B> (full control incl. enforcement toggle and digests) and <B>operator</B> (read + support tools).</>,
            <>Header → <B>Security</B> manages your own password and TOTP 2FA. Enable 2FA on every real account.</>,
            <>Header → <B>Smoke</B> (<HeartPulse className="w-3.5 h-3.5 inline text-rose-400" />) runs a quick live health check of core APIs after a deploy.</>,
          ]} />
        </Card>

        <Card testid="pm-deploy">
          <Title icon={Activity} color="#64748B">8. Preview vs Production</Title>
          <P>Engineering changes land in the <B>Preview</B> environment first. Production runs at <B>hinestime.com</B>. After a redeploy: run <B>Smoke</B>, check the <B>Stripe connected</B> light, and if webhooks were touched, click <B>Repair webhook</B> in production and resend a failed event from the Stripe Dashboard to confirm a 200.</P>
        </Card>

        <div className="text-center text-xs text-slate-500 pt-2 flex items-center justify-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" /> Platform HQ Manual · internal use only · not visible to customers
        </div>
      </main>
    </div>
  );
}
